import { Link } from "react-router-dom";
import { Phone, Mail, Globe } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { useContent } from "@/content/ContentContext";

const Footer = () => {
  const { content } = useContent();
  const { site } = content;
  return (
    <footer className="relative bg-pine text-cream grain" data-testid="site-footer">
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-10">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <LogoMark size={56} dark />
              <div>
                <p className="font-serif text-2xl font-semibold tracking-wide">{site.brandName}</p>
                <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-gold">
                  {site.brandSub}
                </p>
              </div>
            </div>
            <p className="mt-6 font-serif text-2xl italic text-cream/85 leading-snug max-w-sm">
              {site.tagline}
            </p>
            <p className="mt-5 text-sm text-cream/55 leading-relaxed max-w-sm">
              {site.footerBlurb}
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-gold mb-6">Explore</p>
            <nav className="flex flex-col gap-3" aria-label="Footer">
              {[
                { to: "/", label: site.nav.home, id: "home" },
                { to: "/about", label: site.nav.about, id: "about" },
                { to: "/services", label: site.nav.services, id: "services" },
                { to: "/training", label: site.nav.training, id: "training" },
                { to: "/contact", label: site.nav.contact, id: "contact" },
              ].map((l) => (
                <Link
                  key={l.id}
                  to={l.to}
                  data-testid={`footer-link-${l.id}`}
                  className="text-sm text-cream/70 hover:text-gold transition-colors duration-300 w-fit link-underline-gold pb-0.5"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-gold mb-6">Contact</p>
            <p className="text-sm text-cream/85 font-medium mb-4">Paddy Kevane</p>
            <a
              href={`tel:${site.phoneHref}`}
              data-testid="footer-phone-link"
              className="flex items-center gap-3 text-sm text-cream/70 hover:text-gold transition-colors duration-300 py-1.5 w-fit"
            >
              <Phone size={15} className="text-gold" /> {site.phone}
            </a>
            <a
              href={`mailto:${site.email}`}
              data-testid="footer-email-link"
              className="flex items-center gap-3 text-sm text-cream/70 hover:text-gold transition-colors duration-300 py-1.5 w-fit"
            >
              <Mail size={15} className="text-gold" /> {site.email}
            </a>
            <span className="flex items-center gap-3 text-sm text-cream/70 py-1.5">
              <Globe size={15} className="text-gold" /> {site.domain}
            </span>
          </div>
        </div>

        <div className="mt-16 pt-7 border-t border-cream/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-cream/45 tracking-wide">
          <p>© {new Date().getFullYear()} {site.brandName.charAt(0) + site.brandName.slice(1).toLowerCase()} {site.brandSub}. All rights reserved.</p>
          <p className="flex items-center gap-4">
            <span>{site.footerNote}</span>
            <Link to="/admin" data-testid="footer-admin-link" className="hover:text-gold transition-colors duration-300">
              Admin
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
